/**
 * BudgetView — Fleet Budget Planning & Control
 *
 * 3 tabs: Synthèse (comparison), Par Véhicule (ranking), Dérives & Alertes
 * Budget creation modal with auto-fill from contracts/history/TVS
 */
import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Wallet, Plus, RefreshCw, Loader2, Lock, CheckCircle, AlertTriangle,
  TrendingUp, TrendingDown, Minus, Car, Search, Zap, FileText, Calculator
} from 'lucide-react';
import { budgetAPI } from '../../api/index.js';

// Codes only — labels resolved at render via t('budget.*') below (module constants
// are evaluated once, before any language change; see CLAUDE.md i18n traps).
const COST_FAMILY_CODES = [
  'fuel', 'consumable', 'maintenance', 'insurance', 'leasing', 'tax',
  'claims', 'toll', 'parking', 'wash', 'tires', 'other', 'transformation',
];

const SOURCE_CODES = {
  contractual: { color: 'bg-blue-100 text-blue-800' },
  historical: { color: 'bg-amber-100 text-amber-800' },
  calculated: { color: 'bg-green-100 text-green-800' },
  manual: { color: 'bg-gray-100 text-gray-600' },
};

function BudgetView() {
  const { t } = useTranslation();
  // Argent : un seul outil. La devise vient de la ligne (son org) ou du perimetre.
  const { money } = useOrgCurrency();
  const costFamilyLabels = useMemo(
    () => Object.fromEntries(COST_FAMILY_CODES.map((c) => [c, t(`budget.costFamilies.${c}`)])),
    [t]
  );
  const sourceBadges = useMemo(
    () => Object.fromEntries(Object.keys(SOURCE_CODES).map((c) => [
      c, { ...SOURCE_CODES[c], label: t(`budget.sources.${c}`) }
    ])),
    [t]
  );
  const [budgets, setBudgets] = useState([]);
  const [activeBudget, setActiveBudget] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');

  // Creation form state
  const [createLabel, setCreateLabel] = useState('');
  const [createYear, setCreateYear] = useState(new Date().getFullYear());
  const [creating, setCreating] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);
  const [autoFillResult, setAutoFillResult] = useState(null);

  const loadBudgets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await budgetAPI.list();
      const list = res.data || [];
      setBudgets(list);
      // Find active or most recent
      const active = list.find(b => b.status === 'active') || list[0];
      setActiveBudget(active || null);
      if (active) {
        const compRes = await budgetAPI.getComparison(active.id);
        setComparison(compRes.data || null);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBudgets(); }, [loadBudgets]);

  const handleSelectBudget = async (budgetId) => {
    const b = budgets.find(x => x.id === parseInt(budgetId));
    setActiveBudget(b);
    if (b) {
      try {
        const compRes = await budgetAPI.getComparison(b.id);
        setComparison(compRes.data || null);
      } catch { setComparison(null); }
    }
  };

  const handleCreate = async () => {
    if (!createLabel) return;
    setCreating(true);
    try {
      const res = await budgetAPI.create({ label: createLabel, fiscal_year: createYear });
      const newBudget = res.data;
      // Auto-fill immediately
      setAutoFilling(true);
      try {
        const afRes = await budgetAPI.autoFill(newBudget.id);
        setAutoFillResult(afRes.data);
      } catch { /* non-blocking */ }
      setAutoFilling(false);
      setShowCreate(false);
      setCreateLabel('');
      await loadBudgets();
    } catch {
      // error
    } finally {
      setCreating(false);
    }
  };

  const handleActivate = async () => {
    if (!activeBudget) return;
    try {
      await budgetAPI.activate(activeBudget.id);
      await loadBudgets();
    } catch { /* error */ }
  };

  const handleFreeze = async () => {
    if (!activeBudget) return;
    try {
      await budgetAPI.freeze(activeBudget.id);
      await loadBudgets();
    } catch { /* error */ }
  };

  const tabs = [
    { id: 'summary', label: t('budget.tabs.summary'), icon: Calculator },
    { id: 'vehicles', label: t('budget.tabs.vehicles'), icon: Car },
    { id: 'drift', label: t('budget.tabs.drift'), icon: AlertTriangle },
  ];

  // La devise vient du budget lui-même : filiale ⇒ sa devise, holding multi-devise ⇒ référence.
  const budgetCurrency = comparison?.currency ? { currency: comparison.currency } : undefined;
  const formatEur = (n, row) => (n != null ? money(Math.round(n), row || budgetCurrency, { decimals: 0 }) : '—');
  const totals = comparison?.totals || {};
  const lines = comparison?.comparison || [];
  const perVehicle = comparison?.per_vehicle?.vehicles || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Wallet className="w-6 h-6 text-indigo-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{t('budget.header.title')}</h1>
              <p className="text-sm text-gray-500">{t('budget.header.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {budgets.length > 0 && (
              <select
                value={activeBudget?.id || ''}
                onChange={(e) => handleSelectBudget(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {budgets.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.label} (v{b.version_number}) — {b.status}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={() => { setShowCreate(true); setCreateLabel(t('budget.createModal.defaultLabel', { year: createYear })); }}
              className="flex items-center gap-1 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> {t('budget.header.new')}
            </button>
            <button onClick={loadBudgets} className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Tabs */}
        {activeBudget && (
          <div className="flex gap-1 mt-4 border-b border-gray-200">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition ${
                  activeTab === id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
      ) : !activeBudget ? (
        /* Empty state */
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <Wallet className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-700">{t('budget.empty.title')}</h2>
          <p className="text-sm text-gray-500 mt-1 mb-4">{t('budget.empty.subtitle')}</p>
          <button
            onClick={() => { setShowCreate(true); setCreateLabel(t('budget.createModal.defaultLabel', { year: createYear })); }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
          >
            {t('budget.empty.cta')}
          </button>
        </div>
      ) : (
        <>
          {/* Status bar */}
          <div className="flex items-center gap-3">
            <span className={`px-2 py-1 text-xs rounded-full font-medium ${
              activeBudget.status === 'active' ? 'bg-green-100 text-green-800' :
              activeBudget.status === 'frozen' ? 'bg-blue-100 text-blue-800' :
              activeBudget.status === 'draft' ? 'bg-amber-100 text-amber-800' :
              'bg-gray-100 text-gray-600'
            }`}>
              {activeBudget.status === 'active' ? t('budget.status.active') : activeBudget.status === 'frozen' ? t('budget.status.frozen') : activeBudget.status === 'draft' ? t('budget.status.draft') : t('budget.status.archived')}
            </span>
            {activeBudget.confidence_pct != null && (
              <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                activeBudget.confidence_pct >= 70 ? 'bg-green-100 text-green-800' :
                activeBudget.confidence_pct >= 40 ? 'bg-amber-100 text-amber-800' :
                'bg-red-100 text-red-800'
              }`}>
                {t('budget.status.reliability', { pct: activeBudget.confidence_pct })}
              </span>
            )}
            {activeBudget.status === 'draft' && (
              <>
                <button onClick={handleActivate} className="text-xs text-green-600 hover:underline flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> {t('budget.actions.activate')}
                </button>
                <button onClick={handleFreeze} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                  <Lock className="w-3 h-3" /> {t('budget.actions.freeze')}
                </button>
              </>
            )}
          </div>

          {/* ═══ TAB: SYNTHÈSE ═══ */}
          {activeTab === 'summary' && comparison && (
            <div className="space-y-4">
              {/* KPI cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-white rounded-lg shadow-sm p-4 text-center border-l-4 border-indigo-500">
                  <p className="text-xs text-gray-500 uppercase">{t('budget.kpi.budget')}</p>
                  <p className="text-xl font-bold text-indigo-700">{formatEur(totals.planned)}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase">{t('budget.kpi.actual')}</p>
                  <p className="text-xl font-bold text-gray-900">{formatEur(totals.actual)}</p>
                </div>
                <div className={`bg-white rounded-lg shadow-sm p-4 text-center border-l-4 ${totals.variance >= 0 ? 'border-green-500' : 'border-red-500'}`}>
                  <p className="text-xs text-gray-500 uppercase">{t('budget.kpi.variance')}</p>
                  <p className={`text-xl font-bold ${totals.variance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {totals.variance >= 0 ? '+' : ''}{formatEur(totals.variance)}
                  </p>
                  <p className="text-xs text-gray-500">{totals.variance_pct}%</p>
                </div>
                <div className={`bg-white rounded-lg shadow-sm p-4 text-center ${totals.drift_flag ? 'border-l-4 border-red-500' : ''}`}>
                  <p className="text-xs text-gray-500 uppercase">{t('budget.kpi.consumption')}</p>
                  <p className={`text-xl font-bold ${totals.burn_rate_pct > 100 ? 'text-red-700' : 'text-gray-900'}`}>
                    {totals.burn_rate_pct}%
                  </p>
                  <p className="text-xs text-gray-500">{t('budget.kpi.timeElapsed', { pct: totals.time_elapsed_pct })}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase">{t('budget.kpi.landing')}</p>
                  <p className="text-xl font-bold text-gray-900">{formatEur(totals.landing)}</p>
                </div>
              </div>

              {/* Drift warning */}
              {totals.drift_flag && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <p className="text-sm text-red-800 font-medium">
                    {t('budget.driftWarning', { burnRate: totals.burn_rate_pct, timeElapsed: totals.time_elapsed_pct })}
                  </p>
                </div>
              )}

              {/* Comparison table */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('budget.table.item')}</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('budget.table.source')}</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('budget.table.budget')}</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('budget.table.actual')}</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('budget.table.variance')}</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('budget.table.variancePct')}</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('budget.table.landing')}</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('budget.table.status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {lines.map(line => {
                      const src = sourceBadges[line.source_type] || sourceBadges.manual;
                      return (
                        <tr key={line.cost_family} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium">{costFamilyLabels[line.cost_family] || line.cost_family}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`px-1.5 py-0.5 text-xs rounded ${src.color}`}>{src.label}</span>
                          </td>
                          <td className="px-4 py-2 text-right">{formatEur(line.planned)}</td>
                          <td className="px-4 py-2 text-right">{formatEur(line.actual)}</td>
                          <td className={`px-4 py-2 text-right font-semibold ${line.variance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {line.variance >= 0 ? '+' : ''}{formatEur(line.variance)}
                          </td>
                          <td className={`px-4 py-2 text-right ${line.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {line.variance_pct > 0 ? '+' : ''}{line.variance_pct}%
                          </td>
                          <td className="px-4 py-2 text-right">{formatEur(line.landing)}</td>
                          <td className="px-4 py-2 text-center">
                            {line.status === 'over' ? <TrendingUp className="w-4 h-4 text-red-500 mx-auto" /> :
                             line.status === 'under' ? <TrendingDown className="w-4 h-4 text-green-500 mx-auto" /> :
                             <Minus className="w-4 h-4 text-gray-400 mx-auto" />}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 font-semibold">
                    <tr>
                      <td className="px-4 py-2" colSpan={2}>{t('budget.table.total')}</td>
                      <td className="px-4 py-2 text-right">{formatEur(totals.planned)}</td>
                      <td className="px-4 py-2 text-right">{formatEur(totals.actual)}</td>
                      <td className={`px-4 py-2 text-right ${totals.variance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {totals.variance >= 0 ? '+' : ''}{formatEur(totals.variance)}
                      </td>
                      <td className={`px-4 py-2 text-right ${totals.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {totals.variance_pct > 0 ? '+' : ''}{totals.variance_pct}%
                      </td>
                      <td className="px-4 py-2 text-right">{formatEur(totals.landing)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* ═══ TAB: PAR VÉHICULE ═══ */}
          {activeTab === 'vehicles' && (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-3 border-b border-gray-200 flex items-center gap-2">
                <Search className="w-4 h-4 text-gray-400" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder={t('budget.vehiclesTab.searchPlaceholder')} className="flex-1 text-sm border-none focus:ring-0 outline-none" />
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('budget.vehiclesTab.vehicle')}</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('budget.vehiclesTab.costYtd')}</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('budget.vehiclesTab.fuel')}</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('budget.vehiclesTab.maintenance')}</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('budget.vehiclesTab.insurance')}</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('budget.vehiclesTab.taxes')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {perVehicle
                      .filter(v => !search || (v.model || '').toLowerCase().includes(search.toLowerCase()))
                      .sort((a, b) => (b.total || 0) - (a.total || 0))
                      .slice(0, 50)
                      .map((v, i) => (
                        <tr key={v.vehicle_id || i} className="hover:bg-blue-50">
                          <td className="px-3 py-2 font-medium">{v.model || t('budget.vehiclesTab.vehicleFallback', { id: v.vehicle_id })}</td>
                          <td className="px-3 py-2 text-right font-semibold">{formatEur(v.total)}</td>
                          <td className="px-3 py-2 text-right">{formatEur(v.breakdown?.fuel)}</td>
                          <td className="px-3 py-2 text-right">{formatEur(v.breakdown?.maintenance)}</td>
                          <td className="px-3 py-2 text-right">{formatEur(v.breakdown?.insurance)}</td>
                          <td className="px-3 py-2 text-right">{formatEur(v.breakdown?.tax)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ TAB: DÉRIVES & ALERTES ═══ */}
          {activeTab === 'drift' && comparison && (
            <div className="space-y-3">
              {lines.filter(l => l.status === 'over').length === 0 && !totals.drift_flag ? (
                <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                  <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">{t('budget.driftTab.empty')}</p>
                  <p className="text-sm text-gray-400">{t('budget.driftTab.emptySubtitle')}</p>
                </div>
              ) : (
                <>
                  {lines.filter(l => l.status === 'over').sort((a, b) => a.variance - b.variance).map(line => (
                    <div key={line.cost_family} className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-red-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-red-800">{costFamilyLabels[line.cost_family]}</h3>
                          <p className="text-sm text-gray-600">
                            {t('budget.driftTab.actualVsBudget', { actual: formatEur(line.actual), planned: formatEur(line.planned) })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-red-700">{formatEur(Math.abs(line.variance))}</p>
                          <p className="text-xs text-red-600">{t('budget.driftTab.overrun', { pct: Math.abs(line.variance_pct) })}</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {t('budget.driftTab.estimatedLanding', { amount: formatEur(line.landing) })}
                        {line.landing > line.planned ? t('budget.driftTab.overrunLikely') : ''}
                      </p>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* ═══ CREATION MODAL ═══ */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white p-4 rounded-t-xl">
              <h3 className="font-semibold">{t('budget.createModal.title')}</h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('budget.createModal.label')}</label>
                <input type="text" value={createLabel} onChange={e => setCreateLabel(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500" placeholder={t('budget.createModal.labelPlaceholder')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('budget.createModal.fiscalYear')}</label>
                <select value={createYear} onChange={e => setCreateYear(parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500">
                  <option value={2025}>2025</option>
                  <option value={2026}>2026</option>
                  <option value={2027}>2027</option>
                </select>
              </div>
              <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                <p className="text-sm text-indigo-800 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  {t('budget.createModal.autoFillHint')}
                </p>
              </div>
              {autoFillResult && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 font-medium">{t('budget.createModal.prefilled', { amount: formatEur(autoFillResult.total) })}</p>
                  <p className="text-xs text-green-600">
                    {t('budget.createModal.sourceSummary', {
                      contractual: autoFillResult.source_summary?.contractual || 0,
                      historical: autoFillResult.source_summary?.historical || 0,
                      calculated: autoFillResult.source_summary?.calculated || 0,
                    })}
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button onClick={() => { setShowCreate(false); setAutoFillResult(null); }}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">{t('common.actions.cancel')}</button>
                <button onClick={handleCreate}
                  disabled={creating || !createLabel}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
                  {creating || autoFilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {autoFilling ? t('budget.createModal.prefilling') : t('budget.createModal.createAndPrefill')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BudgetView;
